import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from "@angular/core";
import {ValidationHelperService} from "../../../core/helpers/validation-helper.service";
import {FormControl, FormGroup} from "@angular/forms";
import {NotificationService} from "../../../core/services/notification.service";
import {EAction} from "../../../shared/enums/EAction";
import {IRegularExerciseForm} from "../../../shared/interfaces/forms/exercises/IRegularExerciseForm";
import {RegularExerciseHelper} from "../../../core/forms/helpers/RegularExerciseHelper";
import {ExerciseHelper} from "../../../core/helpers/exercise.helper";
import {EBodyPart} from "../../../shared/enums/EBodyPart";
import {ENeurocognitiveType} from "./react-exercise-form/react-exercise-form.component";
import {EExerciseJumpType} from "../../../shared/enums/EExerciseJumpType";
import {EStroopEffectType} from "../../../shared/enums/EStroopEffectType";
import {EAgilityCustomType} from "../../../shared/enums/EAgilityCustomType";
import {ESumType} from "../../../shared/enums/ESumType";
import {ECompanionMode} from "../../../shared/enums/ECompanionMode";
import {ECompanionAgilityType} from "../../../shared/enums/ECompanionAgilityType";
import {ECompanionNumberType} from "../../../shared/enums/ECompanionNumberType";
import {ECompanionCommunicationType} from "../../../shared/enums/ECompanionCommunicationType";
import {ECompanionSimultaneousType} from "../../../shared/enums/ECompanionSimultaneousType";
import {Subscription} from "rxjs";

export interface IRegularExercise {
  id: string
  videoUrl: string
  thumbnailUrl: string
  imageUrl: any
  defaultConfigId: string
  configs: IExerciseConfig[]
  name: string
  description: string
  categoryId: string
  boardType: number
  bodyPart: EBodyPart
  type: number
  isFavorite: boolean
}

export interface IExerciseConfig {
  id: string;
  countdownStart: number;
  countdownStartType: number;
  countdownEndType: number;
  countdownEnd: number;
  sumType: ESumType;
  neurocognitiveType: ENeurocognitiveType;
  reactTo: number[];
  dontReactTo: number[];
  arrows: number[];
  arrowsType: EAgilityCustomType;
  timerDelay: number;
  timerPrompt: number;
  activeSensors: number[];
  sensorsSequence: number[];
  jumpType: EExerciseJumpType;
  numberOfLegs: number;
  jumpCount: number;
  difficulty: number;
  isolatedReact: boolean;
  stroopEffectType: EStroopEffectType;
  liftTime: boolean;
  footFireActivity: boolean;
  verticalReact: boolean;
  isActive: boolean;
  // Companion exercise properties
  modeType?: ECompanionMode;
  companionsCount?: number;
  sensorsCount?: number;
  agilityType?: ECompanionAgilityType;
  numberType?: ECompanionNumberType;
  countIncrementBy?: number;
  countDirectionType?: number;
  complexityLevel?: number;
  reactCommunicationType?: ECompanionCommunicationType;
  simultaneousType?: ECompanionSimultaneousType;
  startValue?: number;
  endValue?: number;
}


@Component({
  selector: "app-exercise-form",
  templateUrl: "./exercise-form.component.html",
  styleUrls: ["./exercise-form.component.scss"],
})
export class ExerciseFormComponent implements OnInit, OnDestroy {
  protected readonly ValidationHelperService = ValidationHelperService;

  @Input() _formGroup: FormGroup<IRegularExerciseForm>;
  @Input() action: EAction;
  @Input() exercise: IRegularExercise;
  @Input() isSubmitting = false;
  @Output() _submit = new EventEmitter();
  @Output() uploadingChange = new EventEmitter<boolean>();
  configFormGroup = new FormGroup({
    selectedConfig: new FormControl(),
  });

  private imageUploading = false;
  private videoUploading = false;
  private configSubscription?: Subscription;

  constructor(
      private notificationService: NotificationService,
      private exerciseHelper: ExerciseHelper) {}

  ngOnInit(): void {
    if(this.action === EAction.Edit) {
      this.configFormGroup.controls.selectedConfig.setValue(this.exercise.configs.find(config => config.id === this.exercise.defaultConfigId));
      this.configSubscription = this.configFormGroup.controls.selectedConfig.valueChanges.subscribe((data) => {
        this.exercise.defaultConfigId = data.id;
        RegularExerciseHelper.patchFormConfig(this._formGroup, this.exercise);
      })
    }
  }

  ngOnDestroy(): void {
    this.configSubscription?.unsubscribe();
  }

  getConfigTitle(config: IExerciseConfig): string {
    return this.exerciseHelper.getConfigTitle(this._formGroup.controls.type.value, config);
  }

  currentForm() {
    switch (this._formGroup.controls.type.value) {
      case 0:
        return this.countForm;
      case 1:
        return this.reactForm;
      case 2:
        return this.sequenceForm;
      case 3:
        return this.agilityForm;
      case 4:
        return this.verticalForm;
      case 5:
        return this.companionForm;
      default:
        this.notificationService.error("Invalid exercise type.");
        this._formGroup.controls.type.setValue(0);
        return this.countForm;
    }
  }

  submitForm() {
    if (this.isSubmitting || this.isUploading) {
      this.notificationService.warn(
        this.isUploading ? "Please wait for media uploads to finish." : "Exercise is already being saved.",
      );
      return;
    }
    if (!RegularExerciseHelper.isFormValid(this._formGroup, this.type)) {
      this._formGroup.markAllAsTouched();
      this.currentForm().markAllAsTouched();
      this.notificationService.error("The form is invalid. Please check.");
      return;
    }
    this.emitSubmit();
  }

  emitSubmit(): void {
    this._submit.emit(
      RegularExerciseHelper.createPayload(this._formGroup, this.exercise),
    );
  }

  updateUploadState(kind: "image" | "video", uploading: boolean): void {
    if (kind === "image") this.imageUploading = uploading;
    else this.videoUploading = uploading;
    this.uploadingChange.emit(this.isUploading);
  }

  get isUploading(): boolean {
    return this.imageUploading || this.videoUploading;
  }

  get category(): FormControl {
    return this._formGroup.get("category") as FormControl;
  }

  get type(): FormControl {
    return this._formGroup.get("type") as FormControl;
  }

  get difficulty(): FormControl {
    return this.exerciseConfig.get("difficulty") as FormControl;
  }

  get numberOfLegs(): FormControl {
    return this.verticalForm.get("numberOfLegs") as FormControl;
  }

  get reactTo(): boolean[] {
    return this.reactForm.get("reactTo").value;
  }

  get react(): number[] {
    return this.reactTo
      .map((value, index) => (value ? index : null))
      .filter((value) => value !== null);
  }

  get dontReactTo(): boolean[] {
    return this.reactForm.get("dontReactTo").value;
  }

  get verticalForm(): FormGroup {
    return this.exerciseConfig.get("verticalForm") as FormGroup;
  }

  get countdownsForm(): FormGroup {
    return this.exerciseConfig.get("countdownForm") as FormGroup;
  }

  get startType(): FormControl {
    return this.countdownsForm.get("startType") as FormControl;
  }

  get startValue(): FormControl {
    return this.countdownsForm.get("startValue") as FormControl;
  }

  get endType(): FormControl {
    return this.countdownsForm.get("endType") as FormControl;
  }

  get endValue(): FormControl {
    return this.countdownsForm.get("endValue") as FormControl;
  }

  get reactForm(): FormGroup {
    return this.exerciseConfig.get("reactForm") as FormGroup;
  }

  get neurocognitiveType(): FormControl {
    return this.reactForm.get("neurocognitiveType") as FormControl;
  }

  get countForm(): FormGroup {
    return this.exerciseConfig.get("countForm") as FormGroup;
  }

  get countType(): FormControl {
    return this.countForm.get("countType") as FormControl;
  }

  get sequenceForm(): FormGroup {
    return this.exerciseConfig.get("sequenceForm") as FormGroup;
  }

  get agilityForm(): FormGroup {
    return this.exerciseConfig.get("agilityForm") as FormGroup;
  }

  get companionForm(): FormGroup {
    return this.exerciseConfig.get("companionForm") as FormGroup;
  }

  get name(): FormControl {
    return this._formGroup.get("name") as FormControl;
  }

  get description(): FormControl {
    return this._formGroup.controls.description;
  }

  get imgThumb(): FormControl {
    return this._formGroup.get("imgThumb") as FormControl;
  }

  get activeSensors(): boolean[] {
    return this.reactForm.get("activeSensors").value;
  }

  get timerPrompt(): FormControl {
    return this.reactForm.get("timerPrompt") as FormControl;
  }

  get sequenceSensors(): number[] {
    return this.sequenceForm.get("sequenceSensors").value;
  }

  get isolatedReact(): FormControl {
    return this.reactForm.get("isolatedReact") as FormControl;
  }

  get exerciseConfig() {
    return this._formGroup.get("config") as FormGroup;
  }

  get bodyPart(): FormControl
  {
    return this._formGroup.get("bodyPart") as FormControl
  }

  protected readonly EAction = EAction;
}
